import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Box,
    Select,
    MenuItem,
    TableFooter,
} from "@mui/material";
import * as Yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import isGHMC from "../../Common/helper/helpar";
import { faTrash } from "@fortawesome/free-solid-svg-icons"; // Removed faEdit
import usetransferstationCategoryStore from "./store/transferstationCategoryStore";
import { ExportTransferstationCategoryData } from "./ExportTransferstationCategory";
import Loader from "../../Common/Loader/loader";

const TransferstationCategory = () => {
    const { transferstationCategory, loading, fetchtransferstationCategory, addTransferstationCategory } = usetransferstationCategoryStore();
    const [isEditing, setIsEditing] = useState(false);
    const [editedRows, setEditedRows] = useState({});
    const [newRowsCount, setNewRowsCount] = useState(0);
    const [errors, setErrors] = useState({});
    const [editIndex, setEditIndex] = useState(null);

    const isProd = isGHMC();

    useEffect(() => {
        fetchtransferstationCategory();
    }, []);

    const validationSchema = Yup.object().shape({
        feedBackName: Yup.string()
            .required("Feedback Name is required")
            .max(100, "Feedback Name must not exceed 100 characters"),
            feedBackRating: Yup.number()
            .test("required", "Feedback Rating is required", (value) => value !== undefined && value !== null && value !== "")
            .typeError("Feedback Rating Required") // Number check after required
            .min(0, "Feedback Rating must be at least 0")
            .max(100, "Feedback Rating cannot be greater than 100"),
        isActive: Yup.boolean().required("Status is required"),
    });

    const validateRow = async (rowData, index) => {
        try {
            await validationSchema.validate(rowData, { abortEarly: false });
            setErrors((prev) => ({ ...prev, [index]: {} }));
            return true;
        } catch (err) {
            const newErrors = {};
            err.inner.forEach((error) => {
                newErrors[error.path] = error.message;
            });
            setErrors((prev) => ({ ...prev, [index]: newErrors }));
            return false;
        }
    };

    const handleEditAll = () => {
        setIsEditing(true);
        const newEditedRows = {};
        transferstationCategory.forEach((row, index) => {
            newEditedRows[index] = {
                feedBackName: row.feedBackName,
                feedBackRating: row.feedBackRating,
                isActive: row.isActive ? true : false,
            };
        });
        setEditedRows(newEditedRows);
    };

    const handleChange = (index, field, value) => {
        let filteredValue = value;
        if (field === "feedBackName") {
            filteredValue = value
                .replace(/[^A-Za-z\s]/g, "")
                .replace(/^\s+/, "")
                .replace(/\s+/g, " ");
        }
        if (field === "feedBackRating") {
            filteredValue = value.replace(/[^0-9]/g, "");
        }

        setEditedRows((prev) => {
            const updatedRow = { ...prev[index], [field]: filteredValue };
            return { ...prev, [index]: updatedRow };
        });
        const rowData = { ...editedRows[index], [field]: filteredValue };
        validateRow(rowData, index);
    };

    const handleAddNew = () => {
        const newIndex = transferstationCategory.length + newRowsCount;
        setNewRowsCount((prev) => prev + 1);
        setIsEditing(true);
        const newEditedRows = { ...editedRows };
        transferstationCategory.forEach((row, index) => {
            if (!newEditedRows[index]) {
                newEditedRows[index] = {
                    feedBackName: row.feedBackName,
                    feedBackRating: row.feedBackRating,
                    isActive: row.isActive ? true : false,
                };
            }
        });
        newEditedRows[newIndex] = { feedBackName: "", feedBackRating: "", isActive: true };
        setEditedRows(newEditedRows);
    };

    const handleDeleteNewRow = (index) => {
        setEditedRows((prev) => {
            const newRows = { ...prev };
            delete newRows[index];
            const reIndexedRows = {};
            let newRowIndex = transferstationCategory.length;
            Object.keys(newRows)
                .sort((a, b) => a - b)
                .forEach((key) => {
                    if (parseInt(key) >= transferstationCategory.length) {
                        reIndexedRows[newRowIndex] = newRows[key];
                        newRowIndex++;
                    } else {
                        reIndexedRows[key] = newRows[key];
                    }
                });
            return reIndexedRows;
        });

        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[index];
            const reIndexedErrors = {};
            let newRowIndex = transferstationCategory.length;
            Object.keys(newErrors)
                .sort((a, b) => a - b)
                .forEach((key) => {
                    if (parseInt(key) >= transferstationCategory.length) {
                        reIndexedErrors[newRowIndex] = newErrors[key];
                        newRowIndex++;
                    } else {
                        reIndexedErrors[key] = newErrors[key];
                    }
                });
            return reIndexedErrors;
        });

        setNewRowsCount((prev) => prev - 1);
        if (Object.keys(editedRows).length === 1) setEditIndex(null);
    };

    const handleSubmit = async () => {
        const validationPromises = Object.keys(editedRows).map((index) =>
            validateRow(editedRows[index], index)
        );
        const validationResults = await Promise.all(validationPromises);

        const hasErrors = !validationResults.every((result) => result === true) ||
            Object.values(errors).some((err) => Object.keys(err).length > 0);

        if (hasErrors) {
            console.log("Validation errors present, API call aborted");
            return;
        }

        const finalData = transferstationCategory.map((row, index) => {
            let updatedRow = { ...row, ...(editedRows[index] || {}) };
            if ("feedBackId" in updatedRow) delete updatedRow.feedBackId;
            return updatedRow;
        });

        Object.keys(editedRows)
            .filter((index) => parseInt(index) >= transferstationCategory.length)
            .forEach((index) => finalData.push(editedRows[index]));

        try {
            const result = await addTransferstationCategory(finalData);
            if (result.success) {
                fetchtransferstationCategory();
                setIsEditing(false);
                setEditedRows({});
                setErrors({});
                setNewRowsCount(0);
                setEditIndex(null);
            }
        } catch (error) {
            console.error("Error submitting:", error);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedRows({});
        setErrors({});
        setNewRowsCount(0);
        setEditIndex(null);
    };

    const headerStyles = { textAlign: 'center', border: '1px solid #ddd', borderRight: 'none', padding: '10px', color: '#fff', backgroundColor: isProd ? "#0d5da5" : "#EC1117", }
    const cellStyles = { textAlign: 'center', border: '1px solid #ddd', borderRight: 'none', padding: '10px', whiteSpace: "normal" };

    let storedPermissions = localStorage.getItem("permissionId");
    const permissionValues1 =
        JSON.parse(localStorage.getItem("rolePermissions")) || {};
    let canWriteValue = null;

    // Convert storedPermissions to a number (if needed)
    storedPermissions = parseInt(storedPermissions, 10);

    // Find the matching roleToPermissionMapping object
    const matchingPermission = permissionValues1.find(
        (mapping) => mapping.permissionId === storedPermissions
    );

    if (matchingPermission) {
        canWriteValue = matchingPermission.canWrite;
        console.log(
            `canWrite value for permission ID ${storedPermissions}:`,
            canWriteValue
        );
    } else {
        console.log(`No match found for permission ID ${storedPermissions}`);
    }



    return (
        <div style={{ width: "100%" }}>
            {loading && <Loader />}
            <div className="content">
                <div className="page-content">
                    <div className="page-header">
                        <div className="d-flex justify-content-end align-items-center mb-3">
                            <div className="d-flex gap-2">
                                {!isEditing && (
                                    <button
                                        className="button-container"
                                        onClick={handleEditAll}
                                    >
                                        Edit All
                                    </button>
                                )}
                                {(isEditing || newRowsCount > 0) && (
                                    <button
                                        className={`btn w-90 ${isProd ? "search-ghmc" : "search-swm"}`}
                                        style={{ border: "none", width: "160px", borderRadius: "5px" }}
                                        onClick={handleCancel}
                                    >
                                        CANCEL
                                    </button>
                                )}
                                <ExportTransferstationCategoryData />
                            </div>
                        </div>
                        <div className="row justify-content-between">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="top-header">
                                            <span className="m-0">Transfer Station Feedback Category</span>
                                        </div>

                                        <TableContainer sx={{ maxHeight: 700 }}>
                                            <Table stickyHeader aria-label="sticky table">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={headerStyles}>S.No</TableCell>
                                                        <TableCell sx={headerStyles}>Feedback Name</TableCell>
                                                        <TableCell sx={headerStyles}>Feedback Rating</TableCell>
                                                        {/* {newRowsCount > 0 && ( */}
                                                        <TableCell sx={headerStyles}>Status</TableCell>
                                                        {/* )} */}
                                                        {newRowsCount > 0 && (
                                                            <TableCell sx={headerStyles}>Action</TableCell>
                                                        )}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {loading ? (
                                                        <TableRow>
                                                            <TableCell colSpan={newRowsCount > 0 ? 5 : 3} align="center">Loading...</TableCell>
                                                        </TableRow>
                                                    ) : transferstationCategory.length === 0 && newRowsCount === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={3} align="center">No data available</TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        Array.from({ length: transferstationCategory.length + newRowsCount }, (_, index) => index).map((index) => (
                                                            <TableRow
                                                                key={index}
                                                                className="p-2"
                                                                style={{
                                                                    backgroundColor: isProd
                                                                        ? index % 2 === 0
                                                                            ? "rgb(143, 201, 255)"
                                                                            : "rgb(174 216 255)"
                                                                        : index % 2 === 0
                                                                            ? "#F8CCCD"
                                                                            : "rgb(255 237 238)",
                                                                }}
                                                            >
                                                                <TableCell sx={cellStyles}>
                                                                    {index + 1}
                                                                </TableCell>
                                                                <TableCell sx={cellStyles} >
                                                                    {isEditing || editedRows[index] ? (
                                                                        <TextField
                                                                            value={editedRows[index]?.feedBackName || ""}
                                                                            onChange={(e) => handleChange(index, "feedBackName", e.target.value)}
                                                                            size="small"
                                                                            error={Boolean(errors[index]?.feedBackName)}
                                                                            helperText={errors[index]?.feedBackName}
                                                                        />
                                                                    ) : (
                                                                        transferstationCategory[index]?.feedBackName || ""
                                                                    )}
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={cellStyles}
                                                                    style={{ color: transferstationCategory[index]?.isActive ? "" : "red" }}
                                                                >
                                                                    {isEditing || editedRows[index] ? (
                                                                        <TextField
                                                                            value={editedRows[index]?.feedBackRating || ""}
                                                                            onChange={(e) => handleChange(index, "feedBackRating", e.target.value)}
                                                                            size="small"
                                                                            error={Boolean(errors[index]?.feedBackRating)}
                                                                            helperText={errors[index]?.feedBackRating}
                                                                        />
                                                                    ) : (
                                                                        transferstationCategory[index]?.feedBackRating || ""
                                                                    )}
                                                                </TableCell>
                                                                {/* {newRowsCount > 0 && ( */}
                                                                <TableCell
                                                                    sx={cellStyles}
                                                                    style={{ color: transferstationCategory[index]?.isActive ? "" : "red" }}
                                                                >
                                                                    {isEditing || editedRows[index] ? (
                                                                        <select
                                                                        value={editedRows[index]?.isActive ?? true}
                                                                            className="form-select"
                                                                            style={{
                                                                                width: "100%",
                                                                                padding: "8px",
                                                                                fontSize: "14px",
                                                                                border: "1px solid #ccc",
                                                                                borderRadius: "6px",
                                                                                backgroundColor: "#fff",
                                                                                cursor: "pointer",
                                                                                outline: "none",
                                                                                appearance: "none",
                                                                                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                                                                                textAlign:'center'
                                                                            }}
                                                                            onChange={(e) => handleChange(index, "isActive", e.target.value === "true")}
                                                                        >
                                                                            <option value={true}>Active</option>
                                                                            <option value={false}>InActive</option>
                                                                        </select>
                                                                    ) : transferstationCategory[index]?.isActive ? (
                                                                        "Active"
                                                                    ) : (
                                                                        "InActive"
                                                                    )}
                                                                </TableCell>
                                                                {/* )} */}
                                                                {newRowsCount > 0 && (
                                                                    <TableCell sx={cellStyles}>
                                                                        {index >= transferstationCategory.length && (
                                                                            <button
                                                                                className={`${index % 2 === 0 ? "bg-F8CCCD" : "bg-ddefff"} ${isProd ? "edit-ghmc" : "edit-swm"}`}
                                                                                onClick={() => handleDeleteNewRow(index)}
                                                                            >
                                                                                <FontAwesomeIcon icon={faTrash} />
                                                                            </button>
                                                                        )}
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        ))
                                                    )}
                                                  
                                                </TableBody>
                                                <TableFooter sx={{width:'100%'}}>
                                                        <TableRow sx={{ position: "sticky", bottom: 0, background: "white", zIndex: 2 }} className={`btn ${isProd ? "rgb(143, 201, 255)" : "rgb(174 216 255)"}`}>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}></TableCell>
                                                        {/* <TableCell colSpan={1} align="right">Total</TableCell> */}
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Total</TableCell>
                                                        <TableCell align="center" className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>
                                                            {transferstationCategory
                                                                .filter((dt) => dt.isActive)
                                                                .reduce((total, row) => total + (parseFloat(row.feedBackRating) || 0), 0)}
                                                        </TableCell>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}></TableCell>
                                                        {newRowsCount > 0 && <TableCell colSpan={1} className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}></TableCell>}
                                                        </TableRow>
                                                    </TableFooter>
                                            </Table>
                                        </TableContainer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Box sx={{ marginTop: "15px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                        {/* {canWriteValue === true ? ( */}
                            <>
                            <button
                                className="button-container"
                               
                            
                                    onClick={handleAddNew}
                                >
                                    + ADD NEW
                                </button>
                                <button
                                    className="deployment-button"
                                    onClick={handleSubmit}
                                    hidden={Object.keys(editedRows).length === 0}

                                >
                                    SAVE
                                </button>
                            </>
                        {/* ) : (
                            ""
                        )} */}
                    </Box>
                </div>
            </div>
        </div>
    );
};

export default TransferstationCategory;